import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { StudioProvider } from 'Contexts/StudioContext'
import { Studio } from 'Core/Studio'
import Spin from 'antd/lib/spin'
import App from 'antd/lib/app'
import Alert from 'antd/lib/alert'
import Button from 'antd/lib/button'
import { Loader } from 'Components/common/Loader/Loader'
import { ProctoredTaskShell } from 'Components/Proctoring/ProctoredTaskShell'
import { ProctoringTopChrome } from 'Components/Proctoring/ProctoringTopChrome'
import { PAGE_TITLES } from 'Constants/page.constants'
import { ContainerType, NotificationPropsType, TaskConfigType } from 'Types/Tasks.types'
import { useFetchAssignment } from 'Hooks/use-assignment'
import { useMonitorKey } from 'Hooks/useMonitorKey'
import { useTitle } from 'Hooks/use-title'
import { useFeatureFlags } from 'Hooks/use-feature-flags'
import { META_PROJECT_ID, NavigationSource } from 'Constants/constants.ts'
import { useQuery } from '@tanstack/react-query'
import { isUserAssociatedWithProject } from 'Services/projects.service'
import { setSendAnalyticsValue } from 'Utils/analytics.utils'
import { useUserProfile } from 'shared/auth/hooks/use-user-profile/useUserProfile'
import type { UseProctoringReturn } from 'proctoring-sdk/index'
import type { MonitorKeyPayload } from 'Utils/monitorKey.utils'
import { isTerminalProctoringSessionStatus } from 'Components/Proctoring/proctoringPermissions.helpers'
import { clearProctoringTimerSnapshot } from 'Utils/proctoringTimer.utils'

type TaskWorkbenchProps = {
    isProctoredSession: boolean
    permissionsGranted?: boolean
    proctoring: UseProctoringReturn | null
    payload: MonitorKeyPayload | null
    endProctoringSession?: () => Promise<void>
    onLeaveProctoredSessionReady?: (fn: () => void) => void
}

const isTaskDataReady = ({
    isProctoredSession,
    assignmentReady,
    isFeatureFlagsLoading,
    hasError,
}: {
    isProctoredSession: boolean
    assignmentReady: boolean
    isFeatureFlagsLoading: boolean
    hasError: boolean
}) =>
    isProctoredSession ? assignmentReady : assignmentReady && !isFeatureFlagsLoading && !hasError

const TaskWorkbench = ({
    isProctoredSession,
    permissionsGranted = true,
    proctoring,
    payload,
    endProctoringSession,
    onLeaveProctoredSessionReady,
}: TaskWorkbenchProps) => {
    const awaitingPermissions = isProctoredSession && !permissionsGranted
    const { id, studioConfigData, refetch, isLoading } = useFetchAssignment()
    const { userProfile } = useUserProfile()
    const studioRef = useRef<Studio | null>(null)
    const studioInstanceRef = useRef<Studio | null>(null)
    const studioIdRef = useRef<string | null>(null)
    const studioMountKeyRef = useRef(0)
    const durationSubmitRef = useRef(false)
    const isTeardownRef = useRef(false)
    const [isTeardown, setIsTeardown] = useState(false)
    const callbacksRef = useRef({
        onNextTask: (_notifications?: NotificationPropsType) => {},
        onExitTask: (_notifications?: NotificationPropsType) => {},
    })

    const setTeardown = useCallback((active: boolean) => {
        isTeardownRef.current = active
        setIsTeardown(active)
    }, [])

    const { data: userAssociationData } = useQuery({
        queryKey: ['userProjectAssociation', META_PROJECT_ID, userProfile.user.user_id],
        queryFn: () =>
            isUserAssociatedWithProject({
                projectId: META_PROJECT_ID,
                userId: userProfile.user.user_id,
            }),
        enabled: !!userProfile.user.user_id && !awaitingPermissions,
        staleTime: 3 * 60 * 1000,
    })

    useEffect(() => {
        setSendAnalyticsValue(!userAssociationData?.is_user_associated)
    }, [userAssociationData])

    const {
        data: featureFlagsDta,
        isLoading: isFeatureFlagsLoading,
        error,
    } = useFeatureFlags(
        (studioConfigData?.taskConfig as TaskConfigType<ContainerType, unknown>)?.assignment
            ?.project_id,
        (studioConfigData?.taskConfig as TaskConfigType<ContainerType, unknown>)?.assignment?.step
            ?.id
    )
    const { notification } = App.useApp()
    const navigate = useNavigate()
    const { stepId } = useParams<{ stepId: string }>()

    const handleNotifications = (notifications?: NotificationPropsType) => {
        if (notifications) {
            const { type, message } = notifications
            switch (type) {
                case 'error':
                    notification.error({
                        message,
                        placement: 'bottomLeft',
                    })
                    break
                case 'success':
                    notification.info({
                        message,
                        placement: 'bottomLeft',
                    })
                    break
            }
        }
    }

    const finalizeProctoring = useCallback(async () => {
        await endProctoringSession?.()
    }, [endProctoringSession])

    const leaveProctoredSession = useCallback(
        (notifications?: NotificationPropsType) => {
            setTeardown(true)
            if (payload?.reference_id != null) {
                clearProctoringTimerSnapshot(String(payload.reference_id))
            }
            studioInstanceRef.current?.destroy?.()
            studioInstanceRef.current = null
            studioIdRef.current = null
            studioRef.current = null
            void finalizeProctoring()

            const searchParams = new URLSearchParams(window.location.search)
            const source = searchParams.get('source')
            if (source === NavigationSource.SAVED_TASKS) {
                navigate(`/saved/${stepId}`, { replace: true })
            } else {
                navigate('/', { replace: true })
            }
            handleNotifications(notifications)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [finalizeProctoring, navigate, payload?.reference_id, setTeardown, stepId]
    )

    useEffect(() => {
        onLeaveProctoredSessionReady?.(() => leaveProctoredSession())
    }, [leaveProctoredSession, onLeaveProctoredSessionReady])

    const onNextTask = useCallback(
        async (notifications?: NotificationPropsType) => {
            durationSubmitRef.current = false
            if (isProctoredSession) {
                leaveProctoredSession(notifications)
                return
            }
            refetch()
            handleNotifications(notifications)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isProctoredSession, refetch, leaveProctoredSession]
    )

    const onExitTask = useCallback(
        (notifications?: NotificationPropsType) => {
            if (isProctoredSession) {
                leaveProctoredSession(notifications)
                return
            }
            const searchParams = new URLSearchParams(window.location.search)
            const source = searchParams.get('source')

            if (source === NavigationSource.SAVED_TASKS) {
                navigate(`/saved/${stepId}`, { replace: true })
            } else {
                navigate('/', { replace: true })
            }
            handleNotifications(notifications)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isProctoredSession, leaveProctoredSession, navigate, stepId]
    )

    callbacksRef.current = { onNextTask, onExitTask }

    const handleDurationEnd = useCallback(async () => {
        if (!isProctoredSession || durationSubmitRef.current) return
        durationSubmitRef.current = true
        const studio = studioRef.current
        const task = studio?.taskManager?.getTask?.() as
            | {
                  submitTask?: (options: {
                      useDummyIfEmpty?: boolean
                      onComplete?: (result?: NotificationPropsType) => void | Promise<void>
                  }) => Promise<void>
              }
            | undefined

        const exitWithError = () => {
            void finalizeProctoring()
            studio?.exitTask({
                type: 'error',
                message: 'Time ended but task could not be submitted.',
            })
        }

        try {
            if (!task?.submitTask) {
                exitWithError()
                return
            }
            await task.submitTask({
                useDummyIfEmpty: true,
                onComplete: (result?: NotificationPropsType) => {
                    void finalizeProctoring()
                    if (result?.type === 'error') {
                        studio?.exitTask(result)
                        return
                    }
                    studio?.exitTask({
                        type: 'success',
                        message: 'Time ended. Task was submitted automatically.',
                    })
                },
            })
        } catch (e) {
            console.error('[proctoring] duration-end submit failed', e)
            exitWithError()
        }
    }, [isProctoredSession, finalizeProctoring])

    useEffect(() => {
        if (!isProctoredSession || !proctoring) return undefined
        return proctoring.events.on('sessionCreated', result => {
            if (!isTerminalProctoringSessionStatus(result?.status)) return
            console.warn('[proctoring] terminal session status — force submitting', result.status)
            void handleDurationEnd()
        })
    }, [isProctoredSession, proctoring, handleDurationEnd])

    const assignmentReady = !!studioConfigData && !isLoading
    const dataReady = isTaskDataReady({
        isProctoredSession,
        assignmentReady,
        isFeatureFlagsLoading,
        hasError: !!error,
    })

    if (isTeardown || isTeardownRef.current) {
        return (
            <div data-testid="task-teardown">
                <Spin indicator={<Loader title={'Finishing session...'} />} />
            </div>
        )
    }

    if (!dataReady || !studioConfigData) {
        if (awaitingPermissions) {
            return null
        }
        return <Spin indicator={<Loader title={'Loading task...'} />} />
    }

    if (studioIdRef.current !== id || !studioInstanceRef.current) {
        studioInstanceRef.current?.destroy?.()
        studioInstanceRef.current = new Studio({
            ...studioConfigData,
            featureFlags: featureFlagsDta,
            onNextTask: notifications => callbacksRef.current.onNextTask(notifications),
            onExitTask: notifications => callbacksRef.current.onExitTask(notifications),
        })
        studioIdRef.current = id
        studioMountKeyRef.current += 1
    }

    const studio = studioInstanceRef.current
    if (!studio) {
        if (awaitingPermissions) {
            return null
        }
        return <Spin indicator={<Loader title={'Loading task...'} />} />
    }
    studioRef.current = studio

    return (
        <div
            data-testid="task-wrapper"
            hidden={awaitingPermissions}
            aria-hidden={awaitingPermissions}
            style={awaitingPermissions ? { display: 'none' } : undefined}
        >
            {isProctoredSession && payload && permissionsGranted ? (
                <ProctoringTopChrome
                    proctoring={proctoring}
                    durationSeconds={payload.duration}
                    referenceId={String(payload.reference_id)}
                    profile={payload.profile}
                    onDurationEnd={handleDurationEnd}
                />
            ) : null}
            <StudioProvider key={`${id}-${studioMountKeyRef.current}`} value={studio}>
                {studio.workbench.element}
            </StudioProvider>
        </div>
    )
}

export const TaskPage = () => {
    const { isResolving, isProctoredSession, payload, error: monitorKeyError } = useMonitorKey()
    const navigate = useNavigate()
    const leaveProctoredSessionRef = useRef<(() => void) | null>(null)
    const registerLeaveProctoredSession = useCallback((fn: () => void) => {
        leaveProctoredSessionRef.current = fn
    }, [])

    useTitle(PAGE_TITLES.TASK)

    if (isResolving) {
        return (
            <div data-testid="monitor-key-resolving">
                <Spin indicator={<Loader title={'Starting proctored session...'} />} />
            </div>
        )
    }

    if (monitorKeyError) {
        return (
            <div style={{ maxWidth: 480, margin: '80px auto', padding: 24 }}>
                <Alert
                    type="error"
                    showIcon
                    message="Unable to start proctored task"
                    description={monitorKeyError}
                />
                <Button
                    style={{ marginTop: 16 }}
                    type="primary"
                    onClick={() => navigate('/', { replace: true })}
                >
                    Back to home
                </Button>
            </div>
        )
    }

    if (!isProctoredSession || !payload) {
        return <TaskWorkbench isProctoredSession={false} proctoring={null} payload={null} />
    }

    return (
        <ProctoredTaskShell
            payload={payload}
            onExitRequested={() => leaveProctoredSessionRef.current?.()}
        >
            {({ proctoring, permissionsGranted, endProctoringSession }) => (
                <TaskWorkbench
                    isProctoredSession
                    permissionsGranted={permissionsGranted}
                    proctoring={proctoring}
                    payload={payload}
                    endProctoringSession={endProctoringSession}
                    onLeaveProctoredSessionReady={registerLeaveProctoredSession}
                />
            )}
        </ProctoredTaskShell>
    )
}
